import React, { useCallback, useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import hotToast from "react-hot-toast";
import api from "../../chat/api";
import ReviewerActionGate from "../../../components/ReviewerActionGate";
import { Spinner } from "./FolderPickerModal";
import KeepConflictDialog from "./KeepConflictDialog";

const SEVERITY = {
  high: { label: "High", color: "#A3141B", bg: "#FDECEC", border: "#E58A8A" },
  medium: { label: "Medium", color: "#7A5B00", bg: "#FFF6D6", border: "#D9B53A" },
  low: { label: "Low", color: "#3F3F46", bg: "#F1F1F3", border: "#A1A1AA" },
};
const FILTERS = [
  ["all", "All"],
  ["high", "High"],
  ["medium", "Medium"],
  ["low", "Low"],
];

const fileIcon = (name = "") => {
  const ext = name.split(".").pop().toLowerCase();
  if (ext === "pdf") return "/pdff.svg";
  if (ext === "xlsx" || ext === "xls") return "/Xls.svg";
  if (ext === "docx" || ext === "doc") return "/docX.svg";
  if (ext === "csv") return "/CSV.svg";
  if (ext === "txt") return "/TXT.svg";
  return "/pdf.svg";
};

const Quote = ({ text }) => {
  const [open, setOpen] = useState(false);
  const long = (text || "").length > 420;
  return (
    <div className="flex flex-col gap-1">
      <p className="m-0 px-3 py-[10px] bg-[#FAFAFA] border border-[#E4E4E7] rounded-md text-[14px] leading-[1.55] text-[#27272A] whitespace-pre-line break-words">
        {/* Clamp the inner text so the padding doesn't show part of the next line. */}
        <span className={long && !open ? "line-clamp-6" : ""}>“{(text || "").trim()}”</span>
      </p>
      {long && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="self-start text-[12px] font-medium text-[#52525B] hover:text-[#A04A97]"
        >
          {open ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
};

const SideAction = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-[13px] font-semibold whitespace-nowrap text-[#A04A97] hover:underline"
  >
    {children}
  </button>
);

// "Keep this one" on either side opens the choice of what happens to the other document.
const Side = ({ side, withDivider, isReviewer, onOpen, onKeep }) => (
  <div className={`px-4 py-3 flex flex-col gap-2 min-w-0 ${withDivider ? "border-r border-[#E4E4E7]" : ""}`}>
    <div className="flex items-center gap-2 min-w-0">
      <img src={fileIcon(side.name)} alt="" className="w-5 h-5 flex-shrink-0" />
      <span title={side.name} className="min-w-0 truncate text-[14px] font-medium text-[#18181B]">
        {side.name}
      </span>
    </div>
    <div className="flex items-center gap-x-4 gap-y-1 flex-wrap">
      {side.file_url && <SideAction onClick={() => onOpen({ ...side, page: null })}>Open</SideAction>}
      <ReviewerActionGate active={isReviewer} place="bottom">
        <SideAction onClick={() => onKeep(side)}>Keep this one</SideAction>
      </ReviewerActionGate>
    </div>
  </div>
);

// One difference: the exact sentence from each file, or the whole passage for older conflicts.
const Passage = ({ side, withDivider, onOpen }) => (
  <div className={`px-4 pt-2 pb-3 flex flex-col gap-1 min-w-0 ${withDivider ? "border-r border-[#E4E4E7]" : ""}`}>
    {side.page != null && side.file_url && (
      <button
        type="button"
        onClick={() => onOpen(side)}
        className="self-start text-[12px] font-medium text-[#52525B] hover:text-[#A04A97] hover:underline"
      >
        Open at page {side.page}
      </button>
    )}
    <Quote text={side.quote || side.text} />
  </div>
);

const SEVERITY_ORDER = ["high", "medium", "low"];

// Every difference between the same two files goes in one card, with each side always in the same column.
const groupByFiles = (items) => {
  const groups = new Map();
  items.forEach((item) => {
    const id = [item.a.doc_id, item.b.doc_id].sort((x, y) => x - y).join("-");
    const group = groups.get(id);
    if (!group) {
      groups.set(id, { id, a: item.a, b: item.b, items: [item] });
      return;
    }
    group.items.push(item.a.doc_id === group.a.doc_id ? item : { ...item, a: item.b, b: item.a });
  });
  return [...groups.values()].map((group) => ({
    ...group,
    severity: SEVERITY_ORDER.find((s) => group.items.some((i) => i.severity === s)) || "medium",
    detected_at: group.items.map((i) => i.detected_at).filter(Boolean).sort().pop(),
  }));
};

// Conflicts page: every pair of documents in the workspace that state different facts.
const ConflictsPage = ({ isReviewer, onBack, onOpenDocument, onCountChange, onDeleted, refreshKey, formatDate }) => {
  const [severity, setSeverity] = useState("all");
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [keeping, setKeeping] = useState(null);
  const latestRequest = useRef(0);

  const load = useCallback(async () => {
    // A slower answer for an earlier filter must not replace the current one.
    const request = ++latestRequest.current;
    setError("");
    try {
      const orgId = Cookies.get("selected_org_id");
      const params = new URLSearchParams();
      if (orgId) params.append("organization_id", orgId);
      if (severity !== "all") params.append("severity", severity);
      const res = await api.get(`/api/v1/rfp/knowledge-base/conflicts/workspace/?${params.toString()}`);
      if (request !== latestRequest.current) return;
      setItems(res.data?.results || []);
      if (severity === "all") onCountChange?.(res.data?.count || 0);
    } catch (err) {
      if (request !== latestRequest.current) return;
      setItems([]);
      setError(err?.response?.data?.error || "Couldn't load conflicts.");
    }
  }, [severity, onCountChange]);

  useEffect(() => {
    setItems(null);
    load();
  }, [load, refreshKey]);

  // Keeping one side deletes the other, which also removes every conflict it had.
  const keepSide = async ({ group, keep, other }) => {
    const item = group.items[0];
    setKeeping(null);
    const toastId = hotToast.loading(`Deleting “${other.name}”...`);
    setBusyId(group.id);
    try {
      const orgId = Cookies.get("selected_org_id");
      await api.post("/api/v1/rfp/knowledge-base/conflicts/keep/", {
        id: item.id,
        keep_key: keep.key,
        outcome: "delete",
        ...(orgId ? { organization_id: orgId } : {}),
      });
      setItems((prev) => (prev || []).filter((i) => i.a.doc_id !== other.doc_id && i.b.doc_id !== other.doc_id));
      onDeleted?.();
      hotToast.success(`Kept “${keep.name}” and deleted “${other.name}”`, { id: toastId });
    } catch (err) {
      hotToast.error(err?.response?.data?.error || "Couldn't update this conflict.", { id: toastId });
      if (err?.response?.status === 409 || err?.response?.status === 404) load();
    } finally {
      setBusyId(null);
    }
  };

  // "Not a problem" for one difference, or for every difference in a card.
  const dismiss = async (busyKey, list) => {
    const toastId = hotToast.loading("Saving...");
    setBusyId(busyKey);
    const orgId = Cookies.get("selected_org_id");
    const results = await Promise.allSettled(
      list.map((item) =>
        api.post("/api/v1/rfp/knowledge-base/conflicts/resolve/", {
          id: item.id,
          action: "dismissed",
          ...(orgId ? { organization_id: orgId } : {}),
        })
      )
    );
    const done = new Set(list.filter((_, i) => results[i].status === "fulfilled").map((item) => item.id));
    setItems((prev) => (prev || []).filter((i) => !done.has(i.id)));
    onCountChange?.((n) => Math.max(0, n - done.size));
    const failed = results.find((r) => r.status === "rejected");
    if (failed) hotToast.error(failed.reason?.response?.data?.error || "Couldn't update this conflict.", { id: toastId });
    else hotToast.success("Marked as not a problem", { id: toastId });
    setBusyId(null);
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col font-outfit">
      {keeping && (
        <KeepConflictDialog
          keep={keeping.keep}
          other={keeping.other}
          onCancel={() => setKeeping(null)}
          onConfirm={() => keepSide(keeping)}
        />
      )}
      <div className="flex flex-col gap-[10px] flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            title="Back to All Files"
            className="w-[30px] h-[30px] flex items-center justify-center border border-[#A1A1AA] rounded-md bg-white hover:border-[#A04A97]"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#52525B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <span onClick={onBack} className="text-[15px] font-medium text-[#52525B] cursor-pointer hover:text-[#A04A97]">
            All Files
          </span>
          <svg width="6" height="9" viewBox="0 0 6 9" fill="#52525B">
            <path d="M3.37 4.5.22 1.35a.79.79 0 0 1 1.13-1.13l3.71 3.71a.8.8 0 0 1 0 1.13L1.35 8.78A.79.79 0 0 1 .22 7.65L3.37 4.5z" />
          </svg>
          <span className="text-[15px] font-semibold text-[#18181B]">Conflicts</span>
        </div>

        <div className="flex items-end justify-between gap-4 border-b border-[#D4D4D8] flex-wrap">
          <div className="flex items-center gap-2 -mb-px px-3 pt-2 pb-[10px] border-b-2 border-[#A04A97] text-[15px] font-semibold text-[#A04A97] whitespace-nowrap">
            <span className="w-2 h-2 rounded-full bg-[#E21919]" />
            Conflicts
            <span className="px-[7px] rounded-full bg-[#F7E9F5] text-[12px] font-semibold leading-[18px] text-[#6E2A67]">
              {items === null ? "…" : items.length}
            </span>
          </div>
          <div className="flex items-center gap-2 pb-2">
            <span className="text-[13px] font-medium text-[#3F3F46] whitespace-nowrap">Severity</span>
            <div className="flex p-[2px] border border-[#A1A1AA] rounded-md bg-white">
              {FILTERS.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSeverity(key)}
                  className={`px-3 py-1 rounded text-[13px] font-medium whitespace-nowrap ${
                    severity === key ? "bg-[#A04A97] text-white" : "text-[#27272A] hover:bg-[#F4F4F5]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <p className="m-0 text-[14px] leading-[1.5] text-[#3F3F46]">
          Two documents in your Knowledge Base state different facts. Sensa may quote either one in a proposal
          until the outdated document is replaced or removed.
        </p>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pt-3 pb-2 flex flex-col gap-3">
        {items === null && (
          <div className="flex items-center gap-2 py-10 justify-center text-[14px] text-[#71717A]">
            <Spinner color="#A04A97" /> Loading conflicts...
          </div>
        )}
        {items !== null && error && <p className="m-0 py-10 text-center text-[14px] text-[#B42318]">{error}</p>}
        {items !== null && !error && items.length === 0 && (
          <div className="py-16 text-center">
            <p className="m-0 mb-1.5 text-[17px] font-semibold text-[#18181B]">
              {severity === "all" ? "No conflicts" : "No conflicts for this filter"}
            </p>
            <p className="m-0 text-[14px] text-[#3F3F46]">
              {severity === "all"
                ? "Sensa hasn't found documents that contradict each other."
                : "Try a different severity."}
            </p>
          </div>
        )}
        {groupByFiles(items || []).map((group) => {
          const sev = SEVERITY[group.severity] || SEVERITY.medium;
          const busy = busyId === group.id;
          const many = group.items.length > 1;
          return (
            <div
              key={group.id}
              className={`flex-shrink-0 border border-[#D4D4D8] rounded-lg overflow-hidden ${busy ? "opacity-60 pointer-events-none" : ""}`}
            >
              <div className="flex items-center gap-[10px] px-4 py-3 border-b border-[#E4E4E7] flex-wrap">
                <span
                  className="flex-shrink-0 px-[10px] py-[2px] rounded-full border text-[12px] font-semibold whitespace-nowrap"
                  style={{ color: sev.color, background: sev.bg, borderColor: sev.border }}
                >
                  {sev.label}
                </span>
                <span className="flex-1 min-w-[200px] text-[15px] font-medium text-[#18181B]">
                  {many ? `${group.items.length} differences between these files` : group.items[0].description}
                </span>
                {group.detected_at && (
                  <span className="flex-shrink-0 text-[13px] text-[#3F3F46] whitespace-nowrap">
                    Detected {formatDate(group.detected_at)}
                  </span>
                )}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <ReviewerActionGate active={isReviewer} place="bottom">
                    <button
                      type="button"
                      onClick={() => dismiss(group.id, group.items)}
                      title="These documents don't really disagree"
                      className="inline-flex items-center gap-1.5 px-3 py-[5px] rounded-md border border-[#A1A1AA] text-[13px] font-medium text-[#27272A] hover:bg-[#F4F4F5] whitespace-nowrap"
                    >
                      {busy && <Spinner size={12} color="#52525B" />}
                      {many ? "None are a problem" : "Not a problem"}
                    </button>
                  </ReviewerActionGate>
                </div>
              </div>
              <div className="grid grid-cols-2 border-b border-[#E4E4E7]">
                {[group.a, group.b].map((side, i) => (
                  <Side
                    key={side.doc_id}
                    side={side}
                    withDivider={i === 0}
                    isReviewer={isReviewer}
                    onOpen={onOpenDocument}
                    onKeep={(s) => {
                      const other = s === group.a ? group.b : group.a;
                      // The dialog counts the other file's conflicts outside this card.
                      const outside = Math.max(1, (other.open_conflicts || 1) - group.items.length + 1);
                      setKeeping({ group, keep: s, other: { ...other, open_conflicts: outside } });
                    }}
                  />
                ))}
              </div>
              {group.items.map((item, n) => {
                const itemSev = SEVERITY[item.severity] || SEVERITY.medium;
                return (
                  <div key={item.id} className={n > 0 ? "border-t border-[#E4E4E7]" : ""}>
                    {many && (
                      <div className="flex items-center gap-[10px] px-4 pt-3 flex-wrap">
                        <span
                          className="flex-shrink-0 px-2 rounded-full border text-[11px] font-semibold leading-[18px] whitespace-nowrap"
                          style={{ color: itemSev.color, background: itemSev.bg, borderColor: itemSev.border }}
                        >
                          {itemSev.label}
                        </span>
                        <span className="flex-1 min-w-[200px] text-[14px] font-medium text-[#18181B]">{item.description}</span>
                        <ReviewerActionGate active={isReviewer} place="bottom">
                          <button
                            type="button"
                            onClick={() => dismiss(group.id, [item])}
                            className="text-[12px] font-medium text-[#52525B] hover:text-[#A04A97] hover:underline whitespace-nowrap"
                          >
                            Not a problem
                          </button>
                        </ReviewerActionGate>
                      </div>
                    )}
                    <div className="grid grid-cols-2">
                      <Passage side={item.a} withDivider onOpen={onOpenDocument} />
                      <Passage side={item.b} onOpen={onOpenDocument} />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ConflictsPage;
